import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { UserService } from '../../services/user/user.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss']
})
export class ChangePasswordComponent {
  show = false;
  changePasswordForm: FormGroup;

  constructor(
    private fb: FormBuilder,
    private userService: UserService,
    private toastr: ToastrService
  ) {
    this.changePasswordForm = this.fb.group({
      currentPassword: ['', [Validators.required]],
      newPassword: ['', [Validators.required, this.passwordValidator]],
      confirmPassword: ['', [Validators.required]]
    }, { validators: this.passwordMatchValidator });
  }

  passwordValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }
    const value = control.value;
    const hasMinLength = value.length > 6;
    const hasNumber = /[0-9]/.test(value);
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value);

    if (!hasMinLength || !hasNumber || !hasSpecialChar) {
      return { invalidPassword: true };
    }
    return null;
  }

  passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const newPassword = control.get('newPassword');
    const confirmPassword = control.get('confirmPassword');

    if (!newPassword || !confirmPassword) {
      return null;
    }

    if (newPassword.value !== confirmPassword.value) {
      return { passwordMismatch: true };
    }
    return null;
  }

  openModal() {
    this.show = true;
    this.changePasswordForm.reset();
  }

  closeModal() {
    this.show = false;
    this.changePasswordForm.reset();
  }

  onSubmit() {
    if (this.changePasswordForm.invalid) {
      this.markFormGroupTouched(this.changePasswordForm);
      return;
    }

    const formValue = this.changePasswordForm.value;
    const changePasswordModel = {
      currentPassword: formValue.currentPassword,
      newPassword: formValue.newPassword
    };

    this.userService.changePassword(changePasswordModel).subscribe({
      next: () => {
        this.toastr.success('Password changed successfully!');
        this.closeModal();
      },
      error: (err) => {
        const errorMessage = err.error?.message || err.message || 'Failed to change password';
        this.toastr.error(errorMessage);
      }
    });
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  get passwordError() {
    const newPassword = this.changePasswordForm.get('newPassword');
    if (newPassword?.hasError('invalidPassword') && newPassword.touched) {
      return 'Password must be more than 6 characters, contain at least one number and one special character';
    }
    return '';
  }

  get confirmPasswordError() {
    const confirmPassword = this.changePasswordForm.get('confirmPassword');
    if (this.changePasswordForm.hasError('passwordMismatch') && confirmPassword?.touched) {
      return 'Passwords do not match';
    }
    return '';
  }
}
